import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {catchError, finalize, map, shareReplay, tap} from 'rxjs/operators';
import {UserMeOutDto} from "../model/user-me.model";
import {UserMeService} from "../service/user-me.service";
import {ROUTE} from "../constant/routing.constant";
import {DomainUserOutDto, MEMBER_TAB, TAB_TYPE, USER_TYPE, UserOutDto} from "../model/user.model";
import {UserTokenFirebaseService} from "../service/firebase/user-token-firebase.service";
import {RoleBaseAccessFirestoreService} from "../service/firebase/role-base-access-firestore.service";
import {MEMBER_TYPE} from "../../setting/common/service/employer-account/member/member.service";
import {AnalyticsService} from "../../../common/service/analytics.service";
import {DEFAULT_ENABLED_MEMBER_TABS} from "../constant/member-tab-access.constant";

@Injectable({
    providedIn: 'root'
})

export class AuthStore {

    private _loggedInUser = new BehaviorSubject<UserMeOutDto | null>(null);
    public loggedInUser: Observable<UserMeOutDto | null>;
    private _accessibleTabs = new BehaviorSubject<TAB_TYPE[] | null>(null);
    public accessibleTabs$: Observable<TAB_TYPE[] | null>;
    private userMeOutDto: UserMeOutDto;
    private inFlightUserMeRequest$: Observable<UserMeOutDto> | null = null;
    private latestEnabledMemberTabs: MEMBER_TAB[] | null = null;
    private inFlightEnabledMemberTabsRequest$: Observable<MEMBER_TAB[]> | null = null;

    constructor(private userMeService: UserMeService,
                private router: Router,
                private userTokenFirebaseService: UserTokenFirebaseService,
                private roleBaseAccessFirestoreService: RoleBaseAccessFirestoreService,
                private analyticsService: AnalyticsService) {
        this.loggedInUser = this._loggedInUser.asObservable();
        this.accessibleTabs$ = this._accessibleTabs.asObservable();
    }

    getLoggedInUser(): Observable<UserMeOutDto> {
        if (this.userMeOutDto) {
            return of(this.userMeOutDto);
        }
        if (this.inFlightUserMeRequest$) {
            return this.inFlightUserMeRequest$;
        }
        this.inFlightUserMeRequest$ = this.getUserFromServer().pipe(
            shareReplay(1),
            finalize(() => this.inFlightUserMeRequest$ = null)
        );
        return this.inFlightUserMeRequest$;
    }

    reload(): void {
        this.reloadLoggedInUser().subscribe();
    }

    reloadLoggedInUser(): Observable<UserMeOutDto> {
        return this.getUserFromServer();
    }

    getToken(): string {
        return localStorage.getItem(ROUTE.TOKEN);
    }

    getUserId(): string {
        return this._loggedInUser.value?.userOutDto?.id;
    }

    getDomainUserOutDto(): DomainUserOutDto {
        return this._loggedInUser.value?.domainUserOutDto;
    }

    getOrganizationId(): string {
        return this._loggedInUser.value?.domainUserOutDto?.organizationMinOutDto?.id;
    }

    getLoggedInUserEmail(): string {
        return this._loggedInUser.value?.userOutDto?.email
    }

    getLoggedInUserFullName(): string {
        return this._loggedInUser.value?.userOutDto?.fullName;
    }

    hasAccessToAiJobAgent(): boolean {
        return !!this._loggedInUser.value?.domainUserOutDto?.hasAccessToAiJobAgent;
    }

    findEnabledMemberTabs(): MEMBER_TAB[] {
        return this.latestEnabledMemberTabs
            ?? this._loggedInUser.value?.domainUserOutDto?.enabledMemberTabs
            ?? DEFAULT_ENABLED_MEMBER_TABS;
    }

    refreshEnabledMemberTabs(): Observable<MEMBER_TAB[]> {
        if (this.inFlightEnabledMemberTabsRequest$) {
            return this.inFlightEnabledMemberTabsRequest$;
        }

        this.inFlightEnabledMemberTabsRequest$ = this.userMeService.find().pipe(
            map((userMeOutDto: UserMeOutDto) => this.rememberEnabledMemberTabs(userMeOutDto)),
            shareReplay(1),
            finalize(() => this.inFlightEnabledMemberTabsRequest$ = null));
        return this.inFlightEnabledMemberTabsRequest$;
    }

    getEmployerMemberId(): string {
        return this._loggedInUser.value?.domainUserOutDto?.memberId;
    }

    getUserType(): USER_TYPE {
        return this.userMeOutDto?.userOutDto?.userType || null;
    }

    getAccessibleTabs(): TAB_TYPE[] | null {
        return this._accessibleTabs.getValue();
    }

    loadAccessibleTabs(organizationId: string, memberType: MEMBER_TYPE): Observable<TAB_TYPE[] | null> {
        return this.roleBaseAccessFirestoreService
            .findVisibleTabs(organizationId, memberType)
            .pipe(
                catchError(() => of(this.roleBaseAccessFirestoreService.resolveDefaultVisibleTabs(memberType))),
                tap((visibleTabs: TAB_TYPE[]) => this._accessibleTabs.next(visibleTabs))
            );
    }

    setCompanyId(id: string): void {
        localStorage.setItem(ROUTE.ORGANIZATION_ID, id);
    }

    clearLocalStorage(): void {
        localStorage.removeItem(ROUTE.TOKEN);
        localStorage.removeItem(ROUTE.ORGANIZATION_ID);
        localStorage.removeItem(ROUTE.BACK_URL);
        localStorage.removeItem(ROUTE.NAVIGATE_BACK_URL);
        localStorage.removeItem(ROUTE.PORTFOLIO_STATE);
        localStorage.clear();
    }

    isLoggedIn(): boolean {
        return this.getToken() !== null;
    }

    logout(): void {
        this.userTokenFirebaseService.delete().then(() => {
            this.clearUserResources();
        }).catch(error => {
            console.log('error:', error);
            this.clearUserResources();
        });
    }

    private clearUserResources() {
        this.analyticsService.resetUser();
        this.clearLocalStorage();
        this.redirectToLogin();
    }

    private getUserFromServer() {
        return this.userMeService.find()
            .pipe(map(userMeOutDto => {
                this.processUserMeData(userMeOutDto);
                return userMeOutDto;
            }));
    }

    private processUserMeData(userMeOutDto: UserMeOutDto) {
        this.registerFirebaseUserToken(userMeOutDto);
        this.userMeOutDto = userMeOutDto;
        this.latestEnabledMemberTabs = null;
        this._loggedInUser.next(userMeOutDto);
        this.identifyAnalyticsUser(userMeOutDto);
    }

    private registerFirebaseUserToken(userMeOutDto: UserMeOutDto): void {
        this.userTokenFirebaseService.create(userMeOutDto?.userOutDto?.id);
    }

    private identifyAnalyticsUser(userMeOutDto: UserMeOutDto): void {
        const userOutDto: UserOutDto = userMeOutDto?.userOutDto;
        if (!userOutDto?.id) {
            return;
        }

        this.analyticsService.identifyUser(userOutDto.id, userOutDto.email, {
            username: userOutDto.username,
            fullName: userOutDto.fullName,
            userType: userOutDto.userType
        });
    }

    private rememberEnabledMemberTabs(userMeOutDto: UserMeOutDto): MEMBER_TAB[] {
        this.latestEnabledMemberTabs = userMeOutDto?.domainUserOutDto?.enabledMemberTabs ?? DEFAULT_ENABLED_MEMBER_TABS;
        return this.latestEnabledMemberTabs;
    }

    private redirectToLogin() {
        this.router.navigate([ROUTE.AUTH, ROUTE.LOGIN]).then(() => {
            window.location.reload();
        });
    }
}
